import React, { useRef } from 'react';
import { styled } from '@mui/material/styles';
import { AddressDto, CompanyDetailedDto, UserDto } from 'models/dtoTypes';

import { GetUsers } from 'services/user';
import { RouteComponentProps, withRouter } from 'react-router';
import { getCompanyApi } from 'services';
import Formsy from 'formsy-react';
import { RemoteChipSelectFormsy } from 'components/formsy';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { CreateOrganisationAssignment } from 'services/organisationAssignment';
import { get } from 'lodash';
import { CurrentUser } from 'auth';
import { useTranslation } from 'react-i18next';
import AlertDialog from 'kosmos/dialogs/AlertDialog';

const PREFIX = 'SmartGovernanceAssignPage';

const classes = {
  root: `${PREFIX}-root`,
  demo: `${PREFIX}-demo`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`&.${classes.demo}`]: {
    backgroundColor: theme.palette.background.paper,
  },
}));

function SmartGovernanceAssignPage(props: RouteComponentProps<{ id: string }>): JSX.Element {
  const companyId = props.match.params.id;
  const [disable, setDisable] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [open, setOpen] = React.useState<boolean>(false);
  const [company, setCompany] = React.useState<CompanyDetailedDto>();
  const formRef = useRef<Formsy>(null);
  const { t } = useTranslation('default');
  const [alert, setAlert] = React.useState<{
    header: string;
    message: string;
    button: string;
  }>({ header: '', message: '', button: '' });

  function getCurrentValues() {
    const formsyEl = formRef.current;
    return formsyEl ? formsyEl.getCurrentValues() : {};
  }

  const disableButton = () => {
    setDisable(true);
  };

  const enableButton = () => {
    setDisable(false);
  };

  React.useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const companyApi = await getCompanyApi();
        const company = (await companyApi.byId(companyId)).data;
        if (!company) {
          console.warn('There is no comapny found for this id');
        } else {
          setCompany(company);
          // if company is not score by kosmos, show notice and block this page
        }
      } catch (e) {
        console.error(e);
      }
      setIsLoading(false);
    };
    loadData();
  }, []);

  const handleSearchUser = async (search: { query: string }) => {
    const resultWithCount = await GetUsers({
      pageIndex: 1,
      fullTextSearch: search.query || '',
      filters: [
        {
          column: 'organisationId',
          value: CurrentUser.getInstance().getOrganisationId(),
        },
        {
          column: 'role',
          value: 'sme',
        },
      ],
      sortColumn: 'name',
    });
    return {
      data: resultWithCount.items || [],
    };
  };

  const renderProperty = (label: string, value: string | number) => {
    return (
      <div style={{ marginTop: 10 }}>
        <label>
          {label}: {value}
        </label>
      </div>
    );
  };

  const generateFullAddress = (address?: AddressDto): string => {
    if (!address) {
      return 'Unknown';
    }
    const { addressLine1, addressLine2, city, country, postalCode } = address;
    const addrComponents = [addressLine1, addressLine2, city, country, postalCode];
    return addrComponents.filter((c) => c).join(', ');
  };

  const handleCancel = () => {
    window.close();
  };

  async function handleCreate(e: React.FormEvent) {
    e.stopPropagation();
    const model = getCurrentValues();
    setSubmitting(true);
    try {
      await CreateOrganisationAssignment({
        kosmosCompanyId: companyId,
        assigneeId: get(model, ['assigneeId', 'alias']) || '',
      });
      setAlert({
        header: 'Assigned',
        message: 'Smart governance has been assigned successfully.',
        button: 'Ok',
      });
      setOpen(true);
    } catch (e) {
      console.error(e);
      setAlert({
        header: 'Not Assigned',
        message: e instanceof Error ? e.message : 'Smart governance assignment has failed.',
        button: 'Ok',
      });
      setOpen(true);
    }
    setSubmitting(false);
  }

  const renderKosmosCompanyPreview = () => {
    const { name, address } = company || {};

    return (
      <div>
        {renderProperty(t('coriolis-company-id'), company?.id || '')}
        {renderProperty(t('company-name'), name || '')}
        {renderProperty(t('registered-office-address'), generateFullAddress(address))}
      </div>
    );
  };

  // nested form is bad, change to element 'div'
  const renderAssignmentForm = () => {
    return (
      <Formsy
        ref={formRef}
        onValid={enableButton}
        onInvalid={disableButton}
        className="form-container flex flex-col justify-center"
      >
        <RemoteChipSelectFormsy
          required={true}
          disabled={isLoading}
          name="assigneeId"
          label={t('assign-to-the-owner-of-the-business-by-entering-the-email-address')}
          placeholder={t('begin-typing-a-business-user-email')}
          onSearchOptions={handleSearchUser}
          optionRenderer={({ id, email }: UserDto) => ({
            alias: id,
            label: email,
          })}
          variant="outlined"
        />

        <Stack direction="row" spacing={2} marginTop={3}>
          <Button variant="outlined" disabled={submitting} onClick={handleCancel} color="primary">
            {t('return-to-search-page')}
          </Button>
          <Button
            variant="contained"
            disabled={submitting || disable}
            color="primary"
            autoFocus
            onClick={handleCreate}
          >
            {t('submit')}
          </Button>
        </Stack>
      </Formsy>
    );
  };

  if (isLoading) {
    return <CircularProgress sx={{ p: 2 }} />;
  }

  return (
    <Root>
      <Stack direction="column" spacing={3}>
        <h2>{t('smart-governance-request')}</h2>
        <Typography variant="h6" sx={{ fontWeight: 'regular' }}>
          {t('smart-governance-can-provide-further-insight-into-company-activity')}
        </Typography>
        <Typography variant="h6">
          {t(
            'the-owner-of-the-business-can-provide-more-information-by-completing-the-smart-governance-questionnaire',
          )}
        </Typography>
        <AlertDialog
          header={alert.header}
          message={alert.message}
          button={alert.button}
          onClose={() => setOpen(false)}
          isOpen={open}
        ></AlertDialog>
        {company && renderKosmosCompanyPreview()}
        {company && renderAssignmentForm()}
      </Stack>
    </Root>
  );
}

export default withRouter(SmartGovernanceAssignPage);
